import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, CreditCard, Users, ArrowRight } from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pricing-page">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Pay per course, learn forever
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              No subscriptions required. Buy only the courses you want and access them forever.
            </p>
          </div>

          {/* How it works for Students */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <Card className="border-slate-200 text-center p-6">
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                Browse Courses
              </h3>
              <p className="text-slate-600">
                Explore hundreds of courses from expert instructors across various topics.
              </p>
            </Card>

            <Card className="border-slate-200 text-center p-6">
              <div className="w-14 h-14 bg-secondary/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-7 h-7 text-secondary-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                Pay Once
              </h3>
              <p className="text-slate-600">
                Purchase individual courses at prices set by instructors. No recurring fees.
              </p>
            </Card>

            <Card className="border-slate-200 text-center p-6">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                Learn Forever
              </h3>
              <p className="text-slate-600">
                Get lifetime access to your purchased courses. Learn at your own pace.
              </p>
            </Card>
          </div>

          {/* CTA Section */}
          <Card className="bg-gradient-to-r from-primary to-purple-600 text-white border-0 overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">
                    Ready to start learning?
                  </h2>
                  <p className="text-white/80 mb-6">
                    Browse our catalog of expert-led courses and find the perfect one for you.
                  </p>
                  <Button 
                    className="bg-white text-primary hover:bg-white/90 h-12 px-8"
                    onClick={() => navigate('/courses')}
                    data-testid="browse-courses-btn"
                  >
                    Browse Courses
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="text-center md:text-right">
                  <div className="inline-block bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <p className="text-white/60 text-sm mb-2">Average course price</p>
                    <p className="font-heading text-4xl font-bold">$9 - $99</p>
                    <p className="text-white/60 text-sm mt-2">One-time payment</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Instructor CTA */}
          <div className="mt-12 text-center p-8 bg-white rounded-xl border border-slate-200">
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
              Want to teach on AISkillFlow?
            </h3>
            <p className="text-slate-600 mb-4">
              Share your expertise and earn 80% of every course sale.
            </p>
            <Button 
              variant="outline"
              onClick={() => navigate('/instructor/pricing')}
              data-testid="instructor-pricing-link"
            >
              View Instructor Plans
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
